import { useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { normalizeAbhaAddress, normalizeAbhaNumber, normalizeMobile } from '@curentia/shared';
import { ErrorText, Field, MIN_PASSWORD, Page, Submit, passwordProblem } from '../components';
import { DOORS, isDoor } from '../lib/doors';
import { useAction, useDevice } from '../state';

export function SignUp() {
  const { door } = useParams();
  const device = useDevice();
  const { busy, error, setError, run } = useAction();
  const [form, setForm] = useState({ name: '', mobile: '', email: '', council: '', number: '', abhaNumber: '', abhaAddress: '', password: '', confirm: '' });
  if (!isDoor(door)) return <Navigate to="/welcome" replace />;
  const info = DOORS[door];
  const registration = 'registration' in info ? info.registration : null;
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const mobile = normalizeMobile(form.mobile);
    if (!form.name.trim()) return setError('Enter your full name.');
    if (!mobile) return setError('Enter a valid Indian mobile number.');
    if (registration && (!form.council.trim() || !form.number.trim())) return setError('Enter your council and registration number.');
    if (form.abhaNumber && !normalizeAbhaNumber(form.abhaNumber)) return setError('An ABHA number has 14 digits.');
    if (form.abhaAddress && !normalizeAbhaAddress(form.abhaAddress)) return setError('Enter an ABHA address such as name@abdm.');
    const problem = passwordProblem(form.password, form.confirm);
    if (problem) return setError(problem);
    void run(() =>
      device.signUp({
        door,
        name: form.name.trim(),
        mobile,
        password: form.password,
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(registration ? { registration: { council: form.council.trim(), number: form.number.trim() } } : {}),
        ...(form.abhaNumber ? { abhaNumber: form.abhaNumber } : {}),
        ...(form.abhaAddress ? { abhaAddress: form.abhaAddress } : {}),
      }),
    );
  };

  return (
    <Page>
      <h1>Create a {info.label.toLowerCase()} account</h1>
      <form onSubmit={submit} noValidate>
        <Field label="Full name" autoComplete="name" value={form.name} onChange={set('name')} required />
        <Field label="Mobile number" type="tel" inputMode="tel" autoComplete="tel" value={form.mobile} onChange={set('mobile')} required />
        <Field label="Email (optional)" type="email" autoComplete="email" value={form.email} onChange={set('email')} />
        {registration ? (
          <>
            <Field label={registration.council} value={form.council} onChange={set('council')} required />
            <Field label={registration.number} value={form.number} onChange={set('number')} required hint="Our verification team checks this before you can use Curentia." />
          </>
        ) : null}
        {door === 'patient' ? (
          <>
            <Field label="ABHA number (optional)" inputMode="numeric" value={form.abhaNumber} onChange={set('abhaNumber')} />
            <Field label="ABHA address (optional)" value={form.abhaAddress} onChange={set('abhaAddress')} hint="For example name@abdm" />
          </>
        ) : null}
        <Field label="Password" type="password" autoComplete="new-password" value={form.password} onChange={set('password')} required hint={`At least ${MIN_PASSWORD} characters. It protects the records on this device, and Curentia cannot recover it for you.`} />
        <Field label="Confirm password" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} required />
        <ErrorText error={error} />
        <Submit busy={busy} busyText="Creating your secure keys…">
          Create account
        </Submit>
      </form>
      <div className="links">
        <Link to={`/login/${door}`}>I already have an account</Link>
        <Link to={`/start/${door}`}>Back</Link>
      </div>
    </Page>
  );
}
